import React, { useCallback } from 'react';

import { ChevronRightIcon, UsersIcon } from 'floorplan/pages/native-floorplan/components/icons';
import PaidLabelPill from 'components/available-now-block/components/available-now-labels/components/paid-label-pill/paid-label-pill';
import { FavoriteStarButton } from 'components/favorite-star-button';
import { IntegrationBadge } from 'components/integration-badge';
import {
  RowCapacity,
  RowChevron,
  RowDiscountBadge,
  RowDiscountIcon,
  RowFavorite,
  RowMeta,
  RowName,
  RowNameLine,
  RowSubtitle,
  RowText,
  RowThumb,
  RowThumbImage,
  StyledResourceRow,
  StyledResourceRowCard,
} from './styles';

interface ResourceListRowProps {
  name: string;
  subtitle: string;
  imageUrl?: string;
  capacity: number;
  isPaid: boolean;
  hasDiscount: boolean;
  isFavorite?: boolean;
  /** `resource.integration`; draws the Tripleseat / Exchange badge beside the name when it is one of those. */
  integration?: string;
  onFavoriteClick?: VoidFunction;
  onClick: VoidFunction;
}

/**
 * The resource as the redesigned list shows it: a row card with a square thumbnail, the name, where
 * it is, and what we know about it — capacity, and the price and discount markers the old tile
 * carried, which would otherwise be lost on the way.
 *
 * Deliberately not the design's full card: the section badge cannot be drawn here at all — sections
 * reach the app only with a floorplan's placements, and the list spans every floor of the building.
 */
export const ResourceListRow = ({
  name,
  subtitle,
  imageUrl,
  capacity,
  isPaid,
  hasDiscount,
  isFavorite,
  integration,
  onFavoriteClick,
  onClick,
}: ResourceListRowProps): JSX.Element => {
  // A div with role=button rather than a real one, so the design's favourite star can sit in the same
  // card without landing inside this element's own subtree (see `StyledResourceRowCard` for why that
  // split matters). The price of that is wiring the keyboard activation a button would have given for
  // free.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  return (
    <StyledResourceRowCard className="resource-tile" data-testid="resource-list-row-card">
      <StyledResourceRow
        data-testid="resource-list-row"
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
      >
        <RowThumb>
          {!!imageUrl && <RowThumbImage src={imageUrl} alt="" />}
          {hasDiscount && (
            <RowDiscountBadge>
              <RowDiscountIcon data-testid="discount-icon" />
            </RowDiscountBadge>
          )}
        </RowThumb>
        <RowText>
          <RowNameLine>
            <RowName data-testid="resource-list-row-name">{name}</RowName>
            <IntegrationBadge integration={integration} />
          </RowNameLine>
          {!!subtitle && <RowSubtitle data-testid="resource-list-row-subtitle">{subtitle}</RowSubtitle>}
          <RowMeta>
            {capacity > 0 && (
              <RowCapacity data-testid={`${name}-capacity`}>
                <UsersIcon />
                {capacity}
              </RowCapacity>
            )}
            {isPaid && <PaidLabelPill />}
          </RowMeta>
        </RowText>
        <RowChevron>
          <ChevronRightIcon />
        </RowChevron>
      </StyledResourceRow>
      {/* Sibling of the row, not a child of it: a real <button> nested inside role="button" would have
          its Enter/Space keydown bubble into the row's own handler above and trigger navigation
          instead of (or as well as) toggling the favorite. */}
      {!!onFavoriteClick && (
        <RowFavorite>
          <FavoriteStarButton isFavorite={!!isFavorite} onClick={onFavoriteClick} />
        </RowFavorite>
      )}
    </StyledResourceRowCard>
  );
};
